import { type AnchorablePayment, type PaymentFrequencyRequest, WeekDay } from '@domains/subscription/priv/Billables/api/api'
import type { CreatePricePluginProps } from '@domains/subscription/priv/Billables/usecases/CreatePriceForm'
import type { LinearPriceRequest } from '@domains/subscription/priv/Billables/usecases/Price/linear/linear'
import { Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material'
import { type Control, Controller, type Path, useForm, useWatch } from 'react-hook-form'

export interface LinearPriceFormProps extends CreatePricePluginProps {
  onCreatePrice: (price: LinearPriceRequest) => void
  currencies: string[]
  defaultPrice?: LinearPrices
}

const priceTypeOptions: Record<LinearPriceFormData['type'], string> = {
  constants: 'Fixed price',
  coefficients: 'Price per unit',
  both: 'Fixed price and price per unit',
}

export interface LinearPrices {
  constants: { value: number; currency: string }[]
  coefficients: { value: number; currency: string }[]
  paymentFrequency: PaymentFrequencyRequest | undefined
}

type ConstantPriceFormData = {
  type: 'constants'
  price: {
    constant: {
      value: number
      currency: string
    }
  }
  paymentFrequency: PaymentFrequencyRequest
}

type CoefficientPriceFormData = {
  type: 'coefficients'
  price: {
    coefficient: {
      value: number
      currency: string
    }
  }
  paymentFrequency: PaymentFrequencyRequest
}

type BothPriceFormData = {
  type: 'both'
  price: {
    constant: {
      value: number
      currency: string
    }
    coefficient: {
      value: number
      currency: string
    }
  }
  paymentFrequency: PaymentFrequencyRequest
}

export type LinearPriceFormData = ConstantPriceFormData | CoefficientPriceFormData | BothPriceFormData

const getDefaultPrice = (
  priceType: LinearPriceFormData['type'],
  defaultPrice?: LinearPrices,
  currency: string = 'SEK',
): LinearPriceFormData => {
  const defaultPaymentFrequency: AnchorablePayment = {
    period: 'monthly',
    type: 'anchorable',
  }

  switch (priceType) {
    case 'constants': {
      return {
        type: 'constants',
        price: { constant: defaultPrice?.constants[0] ?? { value: 0, currency: currency } },
        paymentFrequency: defaultPrice?.paymentFrequency ?? defaultPaymentFrequency,
      }
    }

    case 'coefficients': {
      return {
        type: 'coefficients',
        price: { coefficient: defaultPrice?.coefficients[0] ?? { value: 0, currency: currency } },
        paymentFrequency: defaultPrice?.paymentFrequency ?? defaultPaymentFrequency,
      }
    }
    case 'both': {
      return {
        type: 'both',
        price: {
          constant: defaultPrice?.constants[0] ?? { value: 0, currency: currency },
          coefficient: defaultPrice?.coefficients[0] ?? { value: 0, currency: currency },
        },
        paymentFrequency: defaultPrice?.paymentFrequency ?? defaultPaymentFrequency,
      }
    }
  }
}

const determinePriceType = (defaultPrice: LinearPriceFormProps['defaultPrice']): LinearPriceFormData['type'] => {
  const constants = defaultPrice?.constants.length ?? 0
  const coefficients = defaultPrice?.coefficients.length ?? 0

  switch (true) {
    case constants > 0 && coefficients > 0:
      return 'both'
    case constants > 0:
      return 'constants'
    case coefficients > 0:
      return 'coefficients'
  }

  return 'constants'
}

export const LinearPriceForm = (props: LinearPriceFormProps) => {
  const form = useForm<LinearPriceFormData>({
    values: getDefaultPrice(determinePriceType(props.defaultPrice), props.defaultPrice, props.currencies[0]),
  })

  const handleChangePriceType = (type: LinearPriceFormData['type']) => {
    const { price } = getDefaultPrice(type, props.defaultPrice, props.currencies[0])

    form.setValue('price', price)
  }

  const submitText = props.mode === 'create' ? 'Create' : 'Save changes'

  const mapBillablePriceToFormData = (data: LinearPriceFormData): LinearPriceRequest => {
    return {
      type: 'Linear',
      billableId: props.billableId,
      paymentFrequency: data.paymentFrequency,
      coefficients: data.type === 'coefficients' || data.type === 'both' ? [data.price.coefficient] : [],
      constants: data.type === 'constants' || data.type === 'both' ? [data.price.constant] : [],
    }
  }

  const handleSubmitLinearPriceRequest = (data: LinearPriceFormData) => {
    props.onCreatePrice?.(mapBillablePriceToFormData(data))
  }

  return (
    <form onChange={form.handleSubmit((data) => props.onPriceChange?.(mapBillablePriceToFormData(data)))}>
      <Stack
        sx={{
          gap: 3,
        }}
      >
        <PriceTypeComponent control={form.control} currencies={props.currencies} onChangePriceType={handleChangePriceType} />
        {props.mode !== 'nested' && (
          <Button type="submit" variant="contained" onClick={form.handleSubmit(handleSubmitLinearPriceRequest)}>
            {submitText}
          </Button>
        )}
      </Stack>
    </form>
  )
}

interface PriceTypeComponentProps {
  control: Control<LinearPriceFormData>
  currencies: string[]
  onChangePriceType?: (type: LinearPriceFormData['type']) => void
}

const PriceTypeComponent = ({ control, currencies, onChangePriceType }: PriceTypeComponentProps) => {
  const type = useWatch({ control, name: 'type' })

  const showFixedPrice = type === 'constants' || type === 'both'
  const showPricePerUnit = type === 'coefficients' || type === 'both'

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <FormControl fullWidth>
        <InputLabel>Price modifier</InputLabel>
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <Select
              slotProps={{ input: { 'aria-label': 'price type' } }}
              label="Price modifier"
              value={field.value}
              onChange={(event) => {
                const value = event.target.value as LinearPriceFormData['type']
                field.onChange(value)
                onChangePriceType?.(value)
              }}
            >
              {Object.entries(priceTypeOptions).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </FormControl>
      {showFixedPrice && (
        <Stack
          direction="row"
          sx={{
            gap: 1,
          }}
        >
          <Controller
            control={control}
            name="price.constant.value"
            render={({ field }) => (
              <TextField
                slotProps={{ htmlInput: { 'aria-label': 'fixed price' } }}
                type="number"
                label="Fixed price"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <FormControl fullWidth>
            <InputLabel>Currency</InputLabel>
            <Controller
              control={control}
              name="price.constant.currency"
              render={({ field }) => (
                <Select
                  slotProps={{ input: { 'aria-label': 'fixed-price-currency' } }}
                  label="Currency"
                  value={field.value}
                  onChange={(event) => field.onChange(event.target.value)}
                >
                  {currencies.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </Stack>
      )}
      {showPricePerUnit && (
        <Stack
          direction="row"
          sx={{
            gap: 1,
          }}
        >
          <Controller
            control={control}
            name="price.coefficient.value"
            render={({ field }) => (
              <TextField
                slotProps={{ htmlInput: { 'aria-label': 'price per unit' } }}
                type="number"
                label="Price per unit"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <FormControl fullWidth>
            <InputLabel>Currency</InputLabel>
            <Controller
              control={control}
              name="price.coefficient.currency"
              render={({ field }) => (
                <Select
                  slotProps={{ input: { 'aria-label': 'price-per-unit-currency' } }}
                  label="Currency"
                  value={field.value}
                  onChange={(event) => field.onChange(event.target.value)}
                >
                  {currencies.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </Stack>
      )}
    </Stack>
  )
}
